<script setup lang="ts">
import { computed, ref } from 'vue'
import { VButton, VButtonGroup, VTextField } from '@/modules/shared/components'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { Distribution } from '@/modules/investing/stores/better-entity-store'
import { useDistributionStore } from '@/modules/investing/stores/distribution-store'
import { format } from 'date-fns'
import { useRoute } from 'vue-router'

const { slug } = useRoute().params as { slug: string }
const modelValue = defineModel<Distribution>()

const props = defineProps<{
  refetch?: () => Promise<void>
}>()

const store = useDistributionStore()
const isFormOpen = ref(false)

const initialState = {
  paid_date: format(new Date(), 'yyyy-MM-dd'),
}
const rules = {
  paid_date: { required },
}
const form = ref({ ...initialState })
const v$ = useVuelidate(rules, form, { $lazy: true })

const isPaid = computed(() => {
  return modelValue.value?.payment_status === 'paid'
})

const isUnpaid = computed(() => !isPaid.value)

const fetchDistribution = async () => {
  if (!modelValue.value?.id) return
  modelValue.value = await store.fetchDistribution(modelValue.value.id, { slug })
  if (props.refetch) await props.refetch()
}

const markAsUnpaid = async () => {
  await store.markPaymentAsUnpaid(modelValue.value.id, { slug })
  await fetchDistribution()
}

const openForm = () => {
  isFormOpen.value = true
}

const closeForm = () => {
  isFormOpen.value = false
  form.value = { ...initialState }
}

const markAsPaid = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = {
    id: modelValue.value?.id,
    paid_date: form.value.paid_date,
  }
  await store.markPaymentAsPaid(payload, { slug })
  await fetchDistribution()

  closeForm()
}
</script>

<template>
  <div>
    <div v-if="isPaid">
      <VButton :click="markAsUnpaid" class="w-full" variant="primary">Mark as Pending</VButton>
    </div>
    <div v-else-if="!isFormOpen && isUnpaid">
      <VButton @click="openForm" class="w-full" variant="primary">Mark as Paid</VButton>
    </div>
    <div v-if="isFormOpen && isUnpaid" class="rounded border border-gray-150 p-4">
      <div class="mb-6">Mark Distribution as Paid</div>
      <div class="mb-6">
        <VTextField v-model="form.paid_date" label="Date" property="date" type="date_without_timezone" :v$="v$" />
      </div>
      <VButtonGroup>
        <VButton :click="closeForm">Cancel</VButton>
        <VButton :click="markAsPaid">Save</VButton>
      </VButtonGroup>
    </div>
  </div>
</template>
